/**
 * Error parsing utilities
 * Handles parsing of Strapi and other API errors into user-friendly messages
 */

/**
 * Pull the human-readable messages out of a Strapi error's `details`. Strapi uses
 * two different shapes depending on where the error comes from:
 *  - `{ errors: [{ path, message, name, value }, ...] }` — yup/entity-validator
 *    errors (e.g. a uniqueness check on register), where `value` can hold the raw
 *    field value the user submitted.
 *  - `{ [field]: string[] }` — a flat map of field name to message list.
 * Only string messages are extracted; anything else (including the `value` field
 * above, which may contain user input) is ignored so it never leaks into the UI or
 * ends up stringified as "[object Object]".
 */
function extractDetailMessages(details: unknown): string[] {
  if (!details || typeof details !== "object") return [];

  const values = Object.values(details as Record<string, unknown>).flat();
  const messages = values.map((value) => {
    if (typeof value === "string") return value;
    if (
      value &&
      typeof value === "object" &&
      typeof (value as { message?: unknown }).message === "string"
    ) {
      return (value as { message: string }).message;
    }
    return null;
  });

  const unique = new Set(
    messages.filter((msg): msg is string => !!msg && msg.trim() !== "")
  );
  return [...unique];
}

/**
 * Parse Strapi error into a user-friendly error message
 * @param error - The error object (can be Error, unknown, or any)
 * @param defaultMessage - Default message to use if error cannot be parsed
 * @returns User-friendly error message string
 */
export function parseStrapiError(
  error: unknown,
  defaultMessage: string
): string {
  // Handle null or undefined
  if (error == null) {
    return defaultMessage;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error.trim() || defaultMessage;
  }

  // Handle Error instances
  if (error instanceof Error) {
    // If error message is empty, return default
    if (!error.message || error.message.trim() === "") {
      return defaultMessage;
    }

    try {
      // Try to parse structured error from Strapi (JSON string in error message)
      const parsed = JSON.parse(error.message);

      if (parsed.details) {
        // Handle Strapi validation errors with details
        const detailMessages = extractDetailMessages(parsed.details);
        // Only use details if there are actual messages, otherwise fall back to parsed.message
        if (detailMessages.length > 0) {
          const result = detailMessages.join(", ");
          return result.trim() || parsed.message || defaultMessage;
        }
        // If details is empty object, use parsed.message instead
        return parsed.message || defaultMessage;
      }

      // Return parsed message if available
      const result = parsed.message || error.message || defaultMessage;
      // Ensure we never return empty string
      return result.trim() || defaultMessage;
    } catch (parseError) {
      // If not JSON, use the error message directly
      const message = error.message.toLowerCase();
      if (message.includes("invalid") || message.includes("incorrect")) {
        return defaultMessage;
      }
      if (message.includes("not found") || message.includes("does not exist")) {
        return defaultMessage;
      }
      const result = error.message || defaultMessage;
      // Ensure we never return empty string
      return result.trim() || defaultMessage;
    }
  }

  // Handle objects with message property
  if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, any>;

    if (errorObj.message) {
      const msg = String(errorObj.message).trim();
      return msg || defaultMessage;
    }

    if (errorObj.error?.message) {
      const msg = String(errorObj.error.message).trim();
      return msg || defaultMessage;
    }

    if (errorObj.response?.data?.error?.message) {
      const msg = String(errorObj.response.data.error.message).trim();
      return msg || defaultMessage;
    }

    if (errorObj.response?.data?.message) {
      const msg = String(errorObj.response.data.message).trim();
      return msg || defaultMessage;
    }
  }

  return defaultMessage;
}

// Strapi's own messages for a duplicate username/email on registration. The
// built-in auth controller says "Email or Username are already taken"; the
// schema-level uniqueness check (e.g. when only the username collides) says
// "This attribute must be unique". Both mean the same thing to the user.
const DUPLICATE_ACCOUNT_MESSAGES = [
  "email or username are already taken",
  "this attribute must be unique",
];

/** Whether a (parsed) registration error means an account with these details already exists. */
export function isDuplicateAccountError(message: string): boolean {
  const normalized = message.toLowerCase();
  return DUPLICATE_ACCOUNT_MESSAGES.some((known) => normalized.includes(known));
}

/** Shown at checkout when the visitor's e-mail address already has an account. */
export const ACCOUNT_EXISTS_AT_CHECKOUT =
  "Er bestaat al een account met dit e-mailadres. Log in om door te gaan.";

const ACCOUNT_NOT_CREATED = "Het account kon niet worden aangemaakt. Probeer het opnieuw.";

/**
 * The message for a visitor whose account could not be created during checkout, from the error
 * registerUser threw. Only Strapi's messages are read, never the submitted values in its details.
 */
export function checkoutAccountErrorMessage(error: unknown): string {
  const message = parseStrapiError(error, "");
  if (isDuplicateAccountError(message)) return ACCOUNT_EXISTS_AT_CHECKOUT;
  return message || ACCOUNT_NOT_CREATED;
}

/** Shown at checkout when the e-mail address has an account and the password is not that account's. */
export const WRONG_PASSWORD_AT_CHECKOUT =
  "Er bestaat al een account met dit e-mailadres, maar het wachtwoord klopt niet. Probeer het opnieuw of vraag een nieuw wachtwoord aan.";

/**
 * The message for a visitor whose e-mail address already has an account and who could not be
 * logged in to it during checkout, from what the login call threw. Strapi answers a wrong password
 * with 400 "Invalid identifier or password"; its own texts are never shown.
 */
export function checkoutLoginErrorMessage(error: unknown): string {
  const status = errorStatus(error);
  const failed = error as { data?: any; response?: { data?: any } } | null | undefined;
  const strapiMessage = String((failed?.data ?? failed?.response?.data)?.error?.message ?? "").toLowerCase();

  if (status === 400 && strapiMessage.includes("invalid identifier or password")) {
    return WRONG_PASSWORD_AT_CHECKOUT;
  }
  if (status === 400 && (strapiMessage.includes("blocked") || strapiMessage.includes("not confirmed"))) {
    return "Er bestaat al een account met dit e-mailadres, maar inloggen is niet mogelijk. Neem contact met ons op.";
  }
  if (status === 429) return "Te veel inlogpogingen. Probeer het over een minuut opnieuw.";
  return "Er bestaat al een account met dit e-mailadres, maar inloggen is nu niet gelukt. Probeer het later opnieuw.";
}

/**
 * An error whose message is written for the visitor (Dutch), e.g. "Log in om je in te schrijven.":
 * subscriptionErrorMessage and subscriptionsLoadErrorMessage pass it on as it is. Any other error
 * (Strapi's answers, a network failure) gets a Dutch message chosen by its HTTP status.
 */
export class SubscriptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SubscriptionError";
  }
}

/** Signing up without being logged in and without the details to create an account. */
export const SUBSCRIPTION_LOGIN_REQUIRED = "Log in om je in te schrijven.";
/** The dashboard's list of the visitor's own sign-ups, without being logged in. */
export const SUBSCRIPTIONS_LOGIN_REQUIRED = "Log in om je inschrijvingen te bekijken.";

/** The HTTP status of a failed Strapi call; redaxios throws the response itself. */
export function errorStatus(error: unknown): number | undefined {
  const failed = error as { status?: unknown; response?: { status?: unknown } } | null | undefined;
  const status = failed?.status ?? failed?.response?.status;
  return typeof status === "number" ? status : undefined;
}

/** The `details.reason` Strapi gives with a refusal, when it gives one. */
export function errorReason(error: unknown): string | undefined {
  const failed = error as { data?: any; response?: { data?: any } } | null | undefined;
  const reason = (failed?.data ?? failed?.response?.data)?.error?.details?.reason;
  return typeof reason === "string" ? reason : undefined;
}

/**
 * The visitor's message for a sign-up that failed (createCampaignSubscription). Strapi's own texts
 * are English and never shown.
 */
export function subscriptionErrorMessage(error: unknown, kind?: "participation" | "donation"): string {
  if (error instanceof SubscriptionError) return error.message;
  const status = errorStatus(error);
  if (kind === "donation") {
    if (status === 409 && errorReason(error) === "concept") return "Dit is een voorbeeldcampagne. Meedoen en doneren kan nog niet.";
    if (status === 409) return "Doneren aan deze campagne kan op dit moment niet.";
    if (status === 400) return "Je donatie kon niet worden gestart. Controleer het bedrag en probeer het opnieuw.";
  }
  if (status === 401 || status === 403) {
    return "Je sessie is verlopen. Log opnieuw in en probeer het nog een keer.";
  }
  if (status === 404) {
    return "Deze campagne is niet gevonden. Ververs de pagina en probeer het opnieuw.";
  }
  if (status === 409 && errorReason(error) === "concept") {
    return "Dit is een voorbeeldcampagne. Meedoen en doneren kan nog niet.";
  }
  if (status === 409) {
    return errorReason(error) === "already_participating"
      ? "Je doet al mee aan deze campagne. Je deelname vind je in je dashboard."
      : "Deze campagne staat niet open voor deelname.";
  }
  if (status !== undefined && status >= 500) return "Serverfout. Probeer het later opnieuw.";
  return "Je inschrijving kon niet worden verstuurd. Probeer het opnieuw.";
}

/** The visitor's message when the list of their own sign-ups could not be loaded (dashboard). */
export function subscriptionsLoadErrorMessage(error: unknown): string {
  if (error instanceof SubscriptionError) return error.message;
  const status = errorStatus(error);
  if (status === 401 || status === 403) return SUBSCRIPTIONS_LOGIN_REQUIRED;
  return "Je inschrijvingen konden niet worden geladen. Probeer het later opnieuw.";
}
